import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import API from '../../../Utils/api';
import {
    AdminSurface, Button, DataTable, ErrorState, Field, LoadingState, PageHeader, Panel, StatusPill
} from '../../../Components/AdminSurface';
import InviteUserPanel from './InviteUserPanel';
import styles from './UserSearchPage.module.css';

const PAGE_SIZE = 10;
const INVITE_ID = 'invite-user-panel';

// Coach and Athlete accounts have a dashboard an Admin can visit; Admin accounts do not.
const dashboardPath = user =>
    user.type === 'Coach' ? `/coach/${user._id}` : user.type === 'Athlete' ? `/athlete/${user._id}` : null;

export default function UserSearchPage() {
    const [users, setUsers] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const toggleRef = useRef(null);
    const navigate = useNavigate();

    const fetchAndSetData = async () => {
        setLoadError(false);
        try {
            const { data } = await API.getAllUsers();
            setUsers(data);
        } catch (error) {
            setLoadError(true);
            toast.error(error?.response?.data?.error || 'Failed to load users.', { position: 'bottom-center' });
        }
    };

    useEffect(() => {
        fetchAndSetData();
    }, []);

    const switchAccountStatus = async user => {
        try {
            const next = user.status === 'Active' ? 'Inactive' : 'Active';
            if (next === 'Inactive') await API.deActivateAccount(user._id);
            else await API.activateAccount(user._id);
            setUsers(list => list.map(u => (u._id === user._id ? { ...u, status: next } : u)));
            toast.success('User was successfully updated!', { position: 'bottom-center' });
        } catch (error) {
            toast.error(error?.response?.data?.error, { position: 'bottom-center' });
        }
    };

    const usersToShow = useMemo(() => {
        if (!users) return [];
        const needle = search.toLowerCase();
        return users.filter(
            u =>
                u.firstName?.toLowerCase().startsWith(needle) ||
                u.lastName?.toLowerCase().startsWith(needle) ||
                u.email?.toLowerCase().startsWith(needle) ||
                u.type?.toLowerCase().startsWith(needle)
        );
    }, [search, users]);

    const pages = Math.max(1, Math.ceil(usersToShow.length / PAGE_SIZE));
    const current = Math.min(page, pages - 1);
    const visible = usersToShow.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

    const closeInvite = () => {
        setInviteOpen(false);
        toggleRef.current?.focus();
    };

    const columns = [
        {
            key: 'user',
            header: 'User',
            render: user => {
                const name = `${user.firstName} ${user.lastName}`;
                const to = dashboardPath(user);
                return (
                    <div className={styles.who}>
                        {to ? (
                            <Link to={to} className={styles.name}>
                                {name}
                            </Link>
                        ) : (
                            <span className={styles.name}>{name}</span>
                        )}
                        <span className={styles.email}>{user.email}</span>
                    </div>
                );
            }
        },
        { key: 'type', header: 'User type' },
        {
            key: 'status',
            header: 'Account status',
            render: user => (
                <Button
                    aria-pressed={user.status === 'Active'}
                    aria-label={`${user.firstName} ${user.lastName} account`}
                    title={user.status === 'Active' ? 'Active – click to deactivate' : 'Inactive – click to activate'}
                    onClick={e => {
                        e.stopPropagation();
                        switchAccountStatus(user);
                    }}
                >
                    <StatusPill tone={user.status === 'Active' ? 'good' : 'neutral'}>{user.status}</StatusPill>
                </Button>
            )
        }
    ];

    let body;
    if (loadError) body = <ErrorState label="users" onRetry={fetchAndSetData} />;
    else if (users === null) body = <LoadingState label="users" />;
    else
        body = (
            <>
                <DataTable
                    caption="Users"
                    columns={columns}
                    rows={visible}
                    rowKey={u => u._id}
                    empty="No records found."
                    onRowClick={user => {
                        const to = dashboardPath(user);
                        if (to) navigate(to);
                    }}
                />
                {usersToShow.length > 0 && (
                    <div className={styles.pager}>
                        <span aria-live="polite">
                            Page {current + 1} of {pages}
                        </span>
                        <Button aria-label="Previous page" disabled={current === 0} onClick={() => setPage(current - 1)}>
                            Previous
                        </Button>
                        <Button aria-label="Next page" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
                            Next
                        </Button>
                    </div>
                )}
            </>
        );

    return (
        <AdminSurface>
            <PageHeader
                title="User management"
                sub={users ? `${users.length} users` : undefined}
                actions={
                    <Button
                        ref={toggleRef}
                        variant="primary"
                        aria-expanded={inviteOpen}
                        aria-controls={INVITE_ID}
                        onClick={() => (inviteOpen ? closeInvite() : setInviteOpen(true))}
                    >
                        Add user
                    </Button>
                }
            />
            {inviteOpen && (
                <Panel title="Invite user">
                    <InviteUserPanel
                        id={INVITE_ID}
                        onCancel={closeInvite}
                        onCreated={newUser => {
                            setUsers(list => [newUser, ...(list || [])]);
                            closeInvite();
                        }}
                    />
                </Panel>
            )}
            <div className={styles.toolbar}>
                <div className={styles.search}>
                    <Field
                        label="Search users"
                        type="search"
                        placeholder="Search…"
                        value={search}
                        onChange={e => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                    />
                </div>
            </div>
            <Panel title="All users" flush>
                {body}
            </Panel>
        </AdminSurface>
    );
}
